/**
 * The team picture as beliefs (K2, WORKBOARD §11 / §13 rule 4): what a team knows of the enemy right
 * now, from the state's knowledge records only — never from the enemy's true positions. Every marker
 * at layers 0 / 1 and every map draws these; the action bot reads the same records (`teamTracks`,
 * `sightings`), so the commander sees what the team sees, at the age the team has it.
 *
 * Sources and how sure they leave you (core/awareness.ts gives the decay by source):
 *   - `seen`  — a teammate has eyes on it this sight period (the shared eyes, `state.teamTracks`);
 *   - `told`  — a teammate saw it, some seconds ago (the same record, aged);
 *   - `drone` — a drone's sighting (`state.sightings`, by 'drone'): an identified unit;
 *   - `sensor` — a tower's radar dot: a position with no identity.
 * One belief per enemy unit — the freshest wins; a radar dot stands alone. Pure.
 */
import type { GameState, Sighting, Vec3 } from '../types';
import { PHYS_DT } from '../constants';
import { confidenceAfterSeconds, SOURCE_CONFIDENCE } from './awareness';
import { dpow } from './dmath';
import { markTasksFor, markPos, markConfidence } from './marks';

export type BeliefSource = 'seen' | 'told' | 'drone' | 'sensor' | 'mark';

export interface Belief {
  /** The enemy unit this is about; absent for a radar dot (hostile, unidentified). */
  unitId?: number;
  /** Its team, when identified. */
  unitTeam?: number;
  planetIndex: number;
  surfaceDir: Vec3;
  pos: Vec3;
  /** The tick the knowledge is from. */
  tick: number;
  source: BeliefSource;
  /** 1 fresh … 0 faded out (a faded belief is not returned). */
  confidence: number;
}

/** Seconds a sighting takes to fade out, by what saw it (presentation; the markers and maps share it). */
export const SIGHTING_FADE_SECONDS: Record<Sighting['by'], number> = { tower: 6, drone: 20, probe: 20, relay: 6 };   // I3: the probe is the cheapest drone; 69.2b: a relay, a standing eye like a tower's, identifies

/** How sure the viewer may be of a sighting this old: 1 when fresh, 0 when it has faded out. */
export function confidenceOf(s: Pick<Sighting, 'by' | 'tick'>, tick: number): number {
  const age = Math.max(0, tick - s.tick) * PHYS_DT;
  return Math.max(0, 1 - age / SIGHTING_FADE_SECONDS[s.by]);
}

/** A shared-eyes track this old: fresh while within one sight period, `told` and decaying after. */
export function trackConfidenceAt(trackTick: number, tick: number, sightPeriod: number): { source: 'seen' | 'told'; confidence: number } {
  const ageTicks = Math.max(0, tick - trackTick);
  if (ageTicks <= sightPeriod) return { source: 'seen', confidence: 1 };
  return { source: 'told', confidence: confidenceAfterSeconds('told', ageTicks * PHYS_DT) };
}

/** Ticks between the action bots' looks (actionBot.ts SIGHT_PERIOD reads it from here): a track this fresh is eyes on. */
export const SIGHT_PERIOD_TICKS = 24;

/** Below this a belief is gone from the picture. */
export const BELIEF_FLOOR = 0.02;

/**
 * Everything team `team` believes about the enemy at `tick`, freshest first. Reads `state.teamTracks[team]`
 * (what the team's eyes have shared) and the team's `state.sightings`; nothing else. `sightPeriod` is how many
 * ticks a track counts as "eyes on" (the bot's SIGHT_PERIOD).
 */
export function teamPicture(state: Pick<GameState, 'players' | 'planets' | 'teamTracks' | 'sightings' | 'squads' | 'config'>, team: number, tick: number, sightPeriod = SIGHT_PERIOD_TICKS): Belief[] {
  const byUnit = new Map<number, Belief>();
  const dots: Belief[] = [];
  const consider = (b: Belief) => {
    if (b.confidence < BELIEF_FLOOR) return;
    if (b.unitId === undefined) { dots.push(b); return; }
    const have = byUnit.get(b.unitId);
    if (!have || b.tick > have.tick || (b.tick === have.tick && b.confidence > have.confidence)) byUnit.set(b.unitId, b);
  };
  const unitOf = new Map(state.players.map(p => [p.id, p] as const));
  for (const [unitId, t] of state.teamTracks?.[team] ?? []) {
    const u = unitOf.get(unitId);
    const { source, confidence } = trackConfidenceAt(t.tick, tick, sightPeriod);
    // the track's planet and surface direction: the unit's own at the time are not recorded, so take the position's nearest planet
    const planetIndex = u?.planetIndex ?? nearestPlanetIndex(state, t.pos);
    const planet = state.planets[planetIndex];
    if (!planet) continue;
    const d = { x: t.pos.x - planet.position.x, y: t.pos.y - planet.position.y, z: t.pos.z - planet.position.z };
    const len = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z) || 1;
    consider({ unitId, unitTeam: u?.teamIndex, planetIndex, surfaceDir: { x: d.x / len, y: d.y / len, z: d.z / len }, pos: t.pos, tick: t.tick, source, confidence });
  }
  for (const s of state.sightings ?? []) {
    if (s.teamIndex !== team) continue;
    const confidence = confidenceOf(s, tick);
    consider({ unitId: s.unitId, unitTeam: s.unitTeam, planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, pos: s.pos, tick: s.tick, source: s.by === 'tower' ? 'sensor' : 'drone', confidence });
  }
  // X2: the team's marks — a commander's fixes, the surest beliefs there are, fading
  for (const m of markTasksFor(state, team)) {
    const pos = markPos(state, m.order);
    if (!pos) continue;
    const u = m.order.unitId !== undefined ? unitOf.get(m.order.unitId) : undefined;
    consider({ ...(m.order.unitId !== undefined ? { unitId: m.order.unitId, unitTeam: u?.teamIndex } : {}), planetIndex: m.order.planetIndex, surfaceDir: m.order.surfaceDir, pos, tick: m.issuedTick, source: 'mark', confidence: markConfidence(m.issuedTick, tick) });
  }
  return [...byUnit.values(), ...dots].sort((a, b) => b.tick - a.tick || (a.unitId ?? -1) - (b.unitId ?? -1));
}

/** A landing zone a side remembers (bots against ships): where its eyes saw an enemy ship come down, or an enemy pad with its ship on it. */
export interface LandingZone { planetIndex: number; surfaceDir: Vec3; tick: number; pad: boolean; confidence: number }

/**
 * A landing zone is a place, not a mover (the coordinator, Oct 20: it "should fade like the picture's structure or site
 * marks … otherwise a landing-zone half-life of a few minutes as its own constant"; the picture has no slower class than
 * evidence's 30 s): as sure as a mark when seen (SOURCE_CONFIDENCE.mark), halving every LANDING_ZONE_HALF_LIFE_S — gone
 * (under BELIEF_FLOOR) about 17 minutes after it was last seen.
 */
export const LANDING_ZONE_HALF_LIFE_S = 180;
export function landingZoneConfidence(seenTick: number, tick: number): number {
  return SOURCE_CONFIDENCE.mark * dpow(0.5, Math.max(0, tick - seenTick) * PHYS_DT / LANDING_ZONE_HALF_LIFE_S);
}

/**
 * Bots against ships (with shipsDestructible): the enemy landing zones `team` remembers at `tick` — `state.landingsSeen`
 * (core/landingIntel.ts: only what its people, drones or towers saw), each as sure as `landingZoneConfidence` has it at its
 * age, freshest first; a faded one is not returned.
 */
export function teamLandingZones(state: Pick<GameState, 'landingsSeen'>, team: number, tick: number): LandingZone[] {
  const out: LandingZone[] = [];
  for (const z of state.landingsSeen?.[team] ?? []) {
    const confidence = landingZoneConfidence(z.tick, tick);
    if (confidence >= BELIEF_FLOOR) out.push({ planetIndex: z.planetIndex, surfaceDir: z.surfaceDir, tick: z.tick, pad: !!z.pad, confidence });
  }
  return out.sort((a, b) => b.tick - a.tick);
}

function nearestPlanetIndex(state: Pick<GameState, 'planets'>, pos: Vec3): number {
  let best = 0, bestD = Infinity;
  state.planets.forEach((p, i) => {
    const dx = pos.x - p.position.x, dy = pos.y - p.position.y, dz = pos.z - p.position.z;
    const d = dx * dx + dy * dy + dz * dz;
    if (d < bestD) { bestD = d; best = i; }
  });
  return best;
}
